import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';

import { ConsoleNavigationLogger } from './console-navigation-logger';
import { NavigationLogger } from './navigation-logger';

/**
 * Subscribes the NavigationLogger seam to Router.events. RxJS is correct here:
 * router events are a genuine stream (D6). The subscription lives for the
 * application's lifetime via takeUntilDestroyed on the environment injector.
 */
export function provideNavigationLogging(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: NavigationLogger, useClass: ConsoleNavigationLogger },
    provideAppInitializer(() => {
      const router = inject(Router);
      const logger = inject(NavigationLogger);
      const destroyRef = inject(DestroyRef);

      let previousUrl: string | null = null;
      let navigationStartedAt: number | null = null;

      router.events.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => {
        if (event instanceof NavigationStart) {
          navigationStartedAt = Date.now();
        } else if (event instanceof NavigationEnd) {
          const now = Date.now();
          const url = matchedRouteTemplate(router.routerState.snapshot.root);
          logger.logNavigation({
            url,
            previousUrl,
            at: new Date(now).toISOString(),
            durationMs: navigationStartedAt === null ? 0 : now - navigationStartedAt,
          });
          previousUrl = url;
          navigationStartedAt = null;
        }
      });
    }),
  ]);
}

function matchedRouteTemplate(root: ActivatedRouteSnapshot): string {
  const segments: string[] = [];
  let route: ActivatedRouteSnapshot | null = root.firstChild;

  while (route) {
    const path = route.routeConfig?.path;
    if (path && path !== '**') {
      segments.push(path);
    } else if (path === '**') {
      segments.push(':unmatched');
    }
    route = route.firstChild;
  }

  return `/${segments.join('/')}`;
}
